import React, { useRef, useEffect } from 'react';
import { distance } from 'framer-motion';

function AIParticle({ src }) {
    const canvasRef = useRef(null);
    const hiddenCanvasRef = useRef(null);
    const particlesRef = useRef([]);
    const mouseRef = useRef({ x: 0, y: 0 });
    const animationFrameRef = useRef();

    function calculateGridSize(width) {
        const baseGridSize = 6;
        const minWidth = 768; // Minimum width for which baseGridSize will be used
        const maxWidth = 1920; // Maximum width for which grid size will be scaled

        if (width <= minWidth) {
            return baseGridSize;
        } else if (width >= maxWidth) {
            return baseGridSize * 2;
        } else {
            const scale = (width - minWidth) / (maxWidth - minWidth);
            return baseGridSize + (baseGridSize * scale);
        }
    }

    useEffect(() => {
        if (!canvasRef.current || !hiddenCanvasRef.current) {
            return;
        }

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        const image = new Image();
        image.src = 'data:image/jpeg;base64,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';
        const displayRatio = 1; // 1 = full resolution, 0.5 = half resolution, etc.

        let particles = [];

        const hiddenCanvas = hiddenCanvasRef.current;
        const hiddenCtx = hiddenCanvas.getContext('2d');

        function updateCanvasSize() {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;

            hiddenCanvas.width = window.innerWidth * displayRatio;
            hiddenCanvas.height = window.innerHeight * displayRatio;
            hiddenCtx.drawImage(image, 0, 0, hiddenCanvas.width, hiddenCanvas.height);

            particles = [];

            const gridSize = calculateGridSize(hiddenCanvas.width);

            for (let y = 0; y < hiddenCanvas.height; y += gridSize) {
                for (let x = 0; x < hiddenCanvas.width; x += gridSize) {
                    const colorData = hiddenCtx.getImageData(x, y, 1, 1).data;
                    const r = colorData[0];
                    const g = colorData[1];
                    const b = colorData[2];
                    const a = colorData[3];

                    if (a > 128) {
                        const particle = new Particle(x, y, r, g, b);
                        particles.push(particle);
                    }
                }
            }
        }

        function handleResize() {
            updateCanvasSize();
        }

        window.addEventListener('resize', handleResize);

        image.onload = () => {
            updateCanvasSize();

            function animate() {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                const offsetX = canvas.width * 0.01; // Offset 0.35 is middle of screen 0.15 is left of screen 0.50 is right of screen
                ctx.save();
                ctx.translate(offsetX, (canvas.height - hiddenCanvas.height) / 2);
                particles.forEach((particle) => {
                    particle.update(mouseRef.current);
                    particle.draw(ctx);
                });
                ctx.restore();
                requestAnimationFrame(animate);
            }

            animate();
        };

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [src]);

    return (
        <div style={{ width: '100vw', height: '100vh', overflow: 'hidden' }}>
            <canvas ref={canvasRef} style={{ position: 'absolute', top:"50px", left: 0, zIndex: 1 }} />
            <canvas ref={hiddenCanvasRef} style={{ display: 'none' }} />
        </div>
    );
}

class Particle {
    constructor(x, y, r, g, b) {
        this.x = x;
        this.y = y;
        this.originalX = x;
        this.originalY = y;
        this.color = `rgb(${r}, ${g}, ${b})`;
        this.size = 5;
        this.angle = Math.random() * Math.PI * 4; // Random angle for circular motion
        this.speed = 8; // Constant speed for particles
        this.radius = Math.random() * 10 + 1; // Random radius for circular motion
        this.vx = 0;
        this.vy = 0;
        this.ease = 0.1;
        this.friction = 0.95;
        this.distanceFromMouse = 100;
    }

    update(mouse) {
        const dx = mouse.x - this.x;
        const dy = mouse.y - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const forceDirectionX = dx / distance;
        const forceDirectionY = dy / distance;

        const maxDistance = 100;
        const force = (maxDistance - distance) / maxDistance;
        
        if (distance < maxDistance) {
            this.vx -= forceDirectionX * force * 2;
            this.vy -= forceDirectionY * force * 2;
        }

        const dx2 = this.originalX - this.x;
        const dy2 = this.originalY - this.y;

        this.vx += dx2 * this.ease;
        this.vy += dy2 * this.ease;

        this.vx *= this.friction;
        this.vy *= this.friction;

        this.x += this.vx;
        this.y += this.vy;
    }

    draw(ctx) {
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.closePath();
        ctx.fill();
    }
}

export default AIParticle
